// Loans & advances (Phase B) — role-adaptive. Employees request and track
// repayable advances/loans (approved via the Approvals inbox, deducted by
// payroll); HR sees all loans + outstanding.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Banknote } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const TYPES = ['Advance', 'Loan'];
const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN')}`;
const tone = (s) => (s === 'Approved' ? 'success' : s === 'Settled' ? 'neutral' : s === 'Rejected' ? 'error' : 'warning');

function LoanCard({ l, showName }) {
  const pct = l.principal ? Math.round((l.repaid / l.principal) * 100) : 0;
  return (
    <Card style={{ padding: 16 }}>
      <div className="flex items-start justify-between" style={{ gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600, color: AG.text }}>{showName ? `${l.employeeName} · ` : ''}{l.type} · {money(l.principal)}</div>
          <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{l.installments} installments · {money(l.monthlyAmount)}/mo{l.reason ? ` · ${l.reason}` : ''}</div>
        </div>
        <Pill tone={tone(l.status)} dot>{l.status}</Pill>
      </div>
      {(l.status === 'Approved' || l.status === 'Settled') && (
        <div style={{ marginTop: 12 }}>
          <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: AG.primary }} />
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: 6, fontSize: 12, color: AG.n500 }}>
            <span>Repaid {money(l.repaid)}</span>
            <span>Outstanding {money(l.outstanding)}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Loans({ showToast }) {
  const { isHR } = useAuth();
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => (isHR ? api.loansAll() : api.myLoans()).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    const principal = Number(form.principal);
    if (!principal || principal <= 0 || busy) return; setBusy(true);
    try { await api.requestLoan({ type: form.type, principal, installments: Number(form.installments) || 1, reason: form.reason }); showToast?.('Request submitted for approval'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title={isHR ? 'Loans & Advances' : 'My Loans'} sub={isHR ? 'All loans & outstanding balances' : 'Request and track advances'} />
        {!isHR && <PrimaryBtn onClick={() => setForm({ type: 'Advance', principal: '', installments: 3, reason: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Request</PrimaryBtn>}
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={Banknote} title="No loans" desc={isHR ? 'Approved loans appear here.' : 'Request a salary advance or loan.'} /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{rows.map((l) => <LoanCard key={l.id} l={l} showName={isHR} />)}</div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request advance / loan</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
                <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <TextField size="small" type="number" label="Installments" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} />
              </div>
              <TextField fullWidth size="small" type="number" label="Amount (Rs)" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              {form.principal > 0 && form.installments > 0 && <div style={{ fontSize: 12, color: AG.n500, marginTop: 8 }}>≈ {money(Math.ceil(Number(form.principal) / Number(form.installments)))} / month</div>}
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={submit} disabled={busy}>Submit</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
